import React, { useState, useEffect, useCallback, useRef } from 'react';
import { dowLong } from '../utils/dayOfWeek';
// eslint-disable-next-line no-unused-vars
import { usePermissions } from '../context/PermissionsContext';
import ShiftReliabilityReport from './ShiftReliabilityReport';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

const ROLE_LABELS = {
  referee: 'Referee',
  benchOfficial: 'Bench Official',
  courtOfficial: 'Court Official',
  coach: 'Coach'
};

export default function Reports() {
  // Permissions imported for access control at App level; no edit controls on this page
  const [activeTab, setActiveTab] = useState('pay-report');

  // === PAY REPORT STATE ===
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Reports date range: at least 2 years back
  const reportsMinDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return d.toISOString().split('T')[0];
  })();
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState('both');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [selectedQualification, setSelectedQualification] = useState('');
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const reportRef = useRef(null);

  // === PAY MATRIX STATE ===
  const _today = new Date().toISOString().split('T')[0];
  const [pmStart, setPmStart] = useState(_today);
  const [pmEnd, setPmEnd] = useState(_today);
  const [pmRoles, setPmRoles] = useState(['referee', 'benchOfficial', 'courtOfficial', 'coach']);
  const [pmPeople, setPmPeople] = useState([]); // empty = all
  const [pmCostType, setPmCostType] = useState('both');
  const [pmDetailed, setPmDetailed] = useState(false);
  const [pmData, setPmData] = useState(null);
  const [pmLoading, setPmLoading] = useState(false);
  const [pmError, setPmError] = useState('');
  const [pmExpanded, setPmExpanded] = useState({});

  // === COACHING ALLOCATION STATE ===
  const [coachingWeekDate, setCoachingWeekDate] = useState('');
  const [coachingData, setCoachingData] = useState(null);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [coachingError, setCoachingError] = useState('');
  const [expandedProgramTypes, setExpandedProgramTypes] = useState({});

  // === PARTICIPANT ATTENDANCE STATE ===
  const [partAttStartDate, setPartAttStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [partAttEndDate, setPartAttEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [partAttProgramType, setPartAttProgramType] = useState('');
  const [partAttParticipant, setPartAttParticipant] = useState('');
  const [partAttData, setPartAttData] = useState(null);
  const [partAttLoading, setPartAttLoading] = useState(false);
  const [partAttError, setPartAttError] = useState('');
  const [programTypes, setProgramTypes] = useState([]);
  const [allParticipants, setAllParticipants] = useState([]);

  // === SESSION PARTICIPATION STATE ===
  const [sessPartStartDate, setSessPartStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [sessPartEndDate, setSessPartEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [sessPartProgramType, setSessPartProgramType] = useState('');
  const [sessPartData, setSessPartData] = useState(null);
  const [sessPartLoading, setSessPartLoading] = useState(false);
  const [sessPartError, setSessPartError] = useState('');
  const [sessPartExpanded, setSessPartExpanded] = useState({}); // programId -> true

  // === COURT SUPERVISORS STATE ===
  // Single-day report: the backend endpoint takes a range, but the UI
  // sends the same date for both ends.
  const [csDate, setCsDate] = useState(new Date().toISOString().split('T')[0]);
  const [csData, setCsData] = useState(null);
  const [csLoading, setCsLoading] = useState(false);
  const [csError, setCsError] = useState('');

  // "2026-09-07" → "Monday, 7 September 2026"
  const csFmtDate = (s) => {
    const [y, m, d] = String(s).split('-').map(Number);
    if (!y || !m || !d) return s;
    return new Date(y, m - 1, d).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const runCourtSupervisors = async () => {
    setCsLoading(true); setCsError(''); setCsData(null);
    try {
      const res = await fetch(`${API_BASE}/reports/court-supervisors?startDate=${csDate}&endDate=${csDate}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setCsData(data);
    } catch (e) { setCsError(e.message || 'Failed to load report'); }
    setCsLoading(false);
  };

  const handleCourtSupervisorsPdf = () => {
    if (!csData) return;
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const gameCells = (r) => `<td>${esc(r.date)} ${esc(r.time)}</td><td>${esc(r.court)}</td><td>${esc(r.division)}</td><td>${esc(r.match)}</td>`;
    const section = (title, rows, head, rowFn) => `
      <h2 style="color:#A4161A;font-size:17px;margin:22px 0 4px;">${title}</h2>
      ${rows.length === 0 ? '<p style="color:#777;font-size:13px;margin:0;">Nothing reported.</p>' : `<table><thead><tr>${head}</tr></thead><tbody>${rows.map(rowFn).join('')}</tbody></table>`}`;
    const html =
      section('Forfeits', csData.forfeits, '<th>Date</th><th>Court</th><th>Division</th><th>Match</th><th>Forfeited by</th>',
        r => `<tr>${gameCells(r)}<td><strong>${esc(r.forfeitTeam)}</strong></td></tr>`) +
      section('Tech Fouls', csData.techFouls, '<th>Date</th><th>Court</th><th>Division</th><th>Match</th><th>Person</th><th>Fouls</th><th>Severity</th><th>Ejection</th><th>Comments</th>',
        r => `<tr>${gameCells(r)}<td>${esc(r.name)}${r.team ? ` (${esc(r.team)})` : ''}</td><td>${r.count}</td><td>${esc(r.severity || 'Low')}</td><td>${r.ejection ? 'Yes' : 'No'}</td><td>${esc(r.comments)}</td></tr>`) +
      section('Injuries', csData.injuries, '<th>Date</th><th>Court</th><th>Division</th><th>Match</th><th>Person</th><th>Injury</th><th>Treatment</th><th>Comments</th>',
        r => `<tr>${gameCells(r)}<td>${esc(r.name)}</td><td>${esc(r.injury)}</td><td>${esc(r.treatment)}</td><td>${esc(r.comments)}</td></tr>`) +
      `<h2 style="color:#A4161A;font-size:17px;margin:22px 0 4px;">General Info</h2>` +
      (csData.dayNotes || []).map(n => `<p style="font-size:13px;margin:0 0 8px;">${csData.startDate !== csData.endDate ? `<strong>${esc(n.date)}:</strong> ` : ''}${esc(n.notes).replace(/\n/g, '<br>')}</p>`).join('') +
      (csData.generalInfo.length === 0
        ? ((csData.dayNotes || []).length === 0 ? '<p style="color:#777;font-size:13px;margin:0;">Nothing reported.</p>' : '')
        : `<table><thead><tr><th>Date</th><th>Court</th><th>Division</th><th>Match</th><th>Notes</th></tr></thead><tbody>${csData.generalInfo.map(r => `<tr>${gameCells(r)}<td>${esc(r.notes).replace(/\n/g, '<br>')}</td></tr>`).join('')}</tbody></table>`);
    openPrintWindow('Court Supervisors Report', `${csFmtDate(csData.startDate)} | ${csData.gamesWithReports} game report(s)`, html);
  };

  const fetchPeople = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/person`);
      if (response.ok) {
        setPeople(await response.json());
      }
    } catch (err) {
      console.error('Error fetching people:', err);
    }
  }, []);

  const fetchProgramTypes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/program-types`);
      if (response.ok) setProgramTypes(await response.json());
    } catch (err) {
      console.error('Error fetching program types:', err);
    }
  }, []);

  const fetchAllParticipants = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/participants`);
      if (response.ok) setAllParticipants(await response.json());
    } catch (err) {
      console.error('Error fetching participants:', err);
    }
  }, []);

  useEffect(() => {
    fetchPeople();
    fetchProgramTypes();
    fetchAllParticipants();
  }, [fetchPeople, fetchProgramTypes, fetchAllParticipants]);

  // Initialize coaching week date to current Monday
  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
    setCoachingWeekDate(monday.toISOString().split('T')[0]);
  }, []);

  // === PAY REPORT FUNCTIONS ===
  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    try {
      setLoading(true);
      setError('');
      let url = `${API_BASE}/reports/pay?startDate=${startDate}&endDate=${endDate}`;
      if (selectedPerson) {
        url += `&personId=${selectedPerson}`;
      }
      if (selectedQualification) {
        url += `&qualificationType=${selectedQualification}`;
      }
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        setError('Failed to generate report');
      }
    } catch (err) {
      setError('Error generating report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Group daily breakdown entries by date for display
  const groupBreakdownByDate = (dailyBreakdown) => {
    const grouped = {};
    dailyBreakdown.forEach(entry => {
      if (!grouped[entry.date]) {
        grouped[entry.date] = [];
      }
      grouped[entry.date].push(entry);
    });
    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const handleDownloadPdf = () => {
    if (!reportData || !reportRef.current) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow pop-ups to download the PDF');
      return;
    }

    const reportTypeLabel = reportType === 'both' ? 'Forecast vs Actual' : reportType === 'forecast' ? 'Forecast Only' : 'Actual Only';

    // Build table rows
    let tableHeaders = '';
    let tableRows = '';

    if (reportType === 'both') {
      tableHeaders = '<th>Name</th><th>Email</th><th>Daily Breakdown</th><th>Forecast</th><th>Actual</th><th>Difference</th>';
      tableRows = reportData.referees.map(referee => {
        const grouped = groupBreakdownByDate(referee.dailyBreakdown);
        const breakdownHtml = grouped.length > 0 ? grouped.map(([date, entries]) => {
          const dateStr = new Date(date).toLocaleDateString('en-AU');
          const dayForecast = entries.reduce((s, e) => s + e.forecast, 0);
          const dayActual = entries.reduce((s, e) => s + e.actual, 0);
          const roleLines = entries.map(e =>
            `<span style="color:#666;font-size:11px;margin-left:10px;">${ROLE_LABELS[e.assignmentType] || e.assignmentType} (${e.qualificationLevel})${e.gameCount > 1 ? ' x' + e.gameCount : ''}: $${e.forecast.toFixed(2)} &rarr; $${e.actual.toFixed(2)}</span>`
          ).join('<br/>');
          return `<strong>${dateStr}</strong>: $${dayForecast.toFixed(2)} &rarr; $${dayActual.toFixed(2)}<br/>${roleLines}`;
        }).join('<br/>') : 'No assignments';
        const diff = referee.actualTotal - referee.forecastTotal;
        const diffColor = diff < 0 ? '#4caf50' : diff > 0 ? '#f44336' : '#666';
        return `<tr><td>${referee.refereeName}</td><td>${referee.email}</td><td style="font-size:12px;">${breakdownHtml}</td><td>$${referee.forecastTotal.toFixed(2)}</td><td>$${referee.actualTotal.toFixed(2)}</td><td style="color:${diffColor}">$${diff.toFixed(2)}</td></tr>`;
      }).join('');
      const gDiff = reportData.actualGrandTotal - reportData.forecastGrandTotal;
      const gDiffColor = gDiff < 0 ? '#4caf50' : gDiff > 0 ? '#f44336' : '#666';
      tableRows += `<tr style="background:#e8eaf6;font-weight:600;"><td colspan="3">Grand Total</td><td>$${reportData.forecastGrandTotal.toFixed(2)}</td><td>$${reportData.actualGrandTotal.toFixed(2)}</td><td style="color:${gDiffColor}">$${gDiff.toFixed(2)}</td></tr>`;
    } else if (reportType === 'forecast') {
      tableHeaders = '<th>Name</th><th>Email</th><th>Daily Breakdown</th><th>Total Forecast</th>';
      tableRows = reportData.referees.map(referee => {
        const grouped = groupBreakdownByDate(referee.dailyBreakdown);
        const breakdownHtml = grouped.length > 0 ? grouped.map(([date, entries]) => {
          const dateStr = new Date(date).toLocaleDateString('en-AU');
          const dayTotal = entries.reduce((s, e) => s + e.forecast, 0);
          const roleLines = entries.map(e =>
            `<span style="color:#666;font-size:11px;margin-left:10px;">${ROLE_LABELS[e.assignmentType] || e.assignmentType} (${e.qualificationLevel})${e.gameCount > 1 ? ' x' + e.gameCount : ''}: $${e.forecast.toFixed(2)}</span>`
          ).join('<br/>');
          return `<strong>${dateStr}</strong>: $${dayTotal.toFixed(2)}<br/>${roleLines}`;
        }).join('<br/>') : 'No assignments';
        return `<tr><td>${referee.refereeName}</td><td>${referee.email}</td><td style="font-size:12px;">${breakdownHtml}</td><td>$${referee.forecastTotal.toFixed(2)}</td></tr>`;
      }).join('');
      tableRows += `<tr style="background:#e8eaf6;font-weight:600;"><td colspan="3">Grand Total</td><td>$${reportData.forecastGrandTotal.toFixed(2)}</td></tr>`;
    } else {
      tableHeaders = '<th>Name</th><th>Email</th><th>Daily Breakdown</th><th>Total Actual</th>';
      tableRows = reportData.referees.map(referee => {
        const grouped = groupBreakdownByDate(referee.dailyBreakdown);
        const breakdownHtml = grouped.length > 0 ? grouped.map(([date, entries]) => {
          const dateStr = new Date(date).toLocaleDateString('en-AU');
          const dayTotal = entries.reduce((s, e) => s + e.actual, 0);
          const roleLines = entries.map(e =>
            `<span style="color:#666;font-size:11px;margin-left:10px;">${ROLE_LABELS[e.assignmentType] || e.assignmentType} (${e.qualificationLevel})${e.gameCount > 1 ? ' x' + e.gameCount : ''}: $${e.actual.toFixed(2)}</span>`
          ).join('<br/>');
          return `<strong>${dateStr}</strong>: $${dayTotal.toFixed(2)}<br/>${roleLines}`;
        }).join('<br/>') : 'No assignments';
        return `<tr><td>${referee.refereeName}</td><td>${referee.email}</td><td style="font-size:12px;">${breakdownHtml}</td><td>$${referee.actualTotal.toFixed(2)}</td></tr>`;
      }).join('');
      tableRows += `<tr style="background:#e8eaf6;font-weight:600;"><td colspan="3">Grand Total</td><td>$${reportData.actualGrandTotal.toFixed(2)}</td></tr>`;
    }

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Pay Report</title><style>
      body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
      h1 { color: #A4161A; font-size: 22px; margin-bottom: 5px; }
      .subtitle { color: #666; font-size: 14px; margin-bottom: 20px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th { background: #A4161A; color: white; text-align: left; padding: 10px; font-size: 13px; }
      td { padding: 8px 10px; border-bottom: 1px solid #e0e0e0; font-size: 13px; vertical-align: top; }
      tr:nth-child(even) { background: #f9f9f9; }
      @media print { body { padding: 0; } }
    </style></head><body>
      <h1>SwishIQ - Pay Report</h1>
      <div class="subtitle">${reportData.startDate} to ${reportData.endDate} | ${reportTypeLabel}</div>
      <table><thead><tr>${tableHeaders}</tr></thead><tbody>${tableRows}</tbody></table>
    </body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  // Render daily breakdown grouped by date with role sub-items
  const renderDailyBreakdown = (dailyBreakdown, mode) => {
    if (!dailyBreakdown || dailyBreakdown.length === 0) return 'No assignments';

    const grouped = groupBreakdownByDate(dailyBreakdown);

    return (
      <div style={{ fontSize: '0.8rem' }}>
        {grouped.map(([date, entries]) => {
          const dateStr = new Date(date).toLocaleDateString('en-AU');
          const dayForecast = entries.reduce((s, e) => s + e.forecast, 0);
          const dayActual = entries.reduce((s, e) => s + e.actual, 0);

          return (
            <div key={date} style={{ marginBottom: '0.4rem' }}>
              <strong>{dateStr}: </strong>
              {mode === 'both' && <span>${dayForecast.toFixed(2)} &rarr; ${dayActual.toFixed(2)}</span>}
              {mode === 'forecast' && <span>${dayForecast.toFixed(2)}</span>}
              {mode === 'actual' && <span>${dayActual.toFixed(2)}</span>}
              <div style={{ paddingLeft: '1rem' }}>
                {entries.map((entry, idx) => (
                  <div key={idx} style={{ color: '#666', fontSize: '0.75rem', lineHeight: '1.4' }}>
                    {ROLE_LABELS[entry.assignmentType] || entry.assignmentType} ({entry.qualificationLevel}){entry.gameCount > 1 ? ` x${entry.gameCount}` : ''}:
                    {mode === 'both' && <span> ${entry.forecast.toFixed(2)} &rarr; ${entry.actual.toFixed(2)}</span>}
                    {mode === 'forecast' && <span> ${entry.forecast.toFixed(2)}</span>}
                    {mode === 'actual' && <span> ${entry.actual.toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // === COACHING ALLOCATION FUNCTIONS ===
  const handleGenerateCoachingReport = async () => {
    if (!coachingWeekDate) {
      setCoachingError('Please select a week start date');
      return;
    }

    try {
      setCoachingLoading(true);
      setCoachingError('');
      const response = await fetch(`${API_BASE}/reports/coaching-allocation?weekStartDate=${coachingWeekDate}`);

      if (response.ok) {
        const data = await response.json();
        setCoachingData(data);
        // Auto-expand all program types
        const expanded = {};
        data.programTypes.forEach(pt => { expanded[pt.programTypeId] = true; });
        setExpandedProgramTypes(expanded);
      } else {
        setCoachingError('Failed to generate coaching allocation report');
      }
    } catch (err) {
      setCoachingError('Error generating report: ' + err.message);
    } finally {
      setCoachingLoading(false);
    }
  };

  const toggleProgramType = (ptId) => {
    setExpandedProgramTypes(prev => ({ ...prev, [ptId]: !prev[ptId] }));
  };

  const navigateWeek = (direction) => {
    const current = new Date(coachingWeekDate);
    current.setDate(current.getDate() + (direction * 7));
    setCoachingWeekDate(current.toISOString().split('T')[0]);
  };

  // === PARTICIPANT ATTENDANCE REPORT ===
  const handleGenerateParticipantAttendanceReport = async () => {
    if (!partAttStartDate || !partAttEndDate) {
      setPartAttError('Please select both start and end dates');
      return;
    }
    try {
      setPartAttLoading(true);
      setPartAttError('');
      let url = `${API_BASE}/reports/participant-attendance?startDate=${partAttStartDate}&endDate=${partAttEndDate}`;
      if (partAttProgramType) url += `&programTypeId=${partAttProgramType}`;
      if (partAttParticipant) url += `&participantId=${partAttParticipant}`;
      const response = await fetch(url);
      if (response.ok) {
        setPartAttData(await response.json());
      } else {
        setPartAttError('Failed to generate report');
      }
    } catch (err) {
      setPartAttError('Error generating report: ' + err.message);
    } finally {
      setPartAttLoading(false);
    }
  };

  // === SESSION PARTICIPATION HANDLERS ===
  const handleGenerateSessionParticipationReport = async () => {
    if (!sessPartStartDate || !sessPartEndDate) {
      setSessPartError('Please select both start and end dates');
      return;
    }
    try {
      setSessPartLoading(true);
      setSessPartError('');
      setSessPartExpanded({});
      let url = `${API_BASE}/reports/session-participation?startDate=${sessPartStartDate}&endDate=${sessPartEndDate}`;
      if (sessPartProgramType) url += `&programTypeId=${sessPartProgramType}`;
      const response = await fetch(url);
      if (response.ok) {
        setSessPartData(await response.json());
      } else {
        setSessPartError('Failed to generate report');
      }
    } catch (err) {
      setSessPartError('Error generating report: ' + err.message);
    } finally {
      setSessPartLoading(false);
    }
  };

  // PDF (print window) export for the new report.
  const handleSessPartPdf = () => {
    if (!sessPartData || !sessPartData.sessions || sessPartData.sessions.length === 0) return;
    const headers = '<th>Date</th><th>Time</th><th>Program</th><th>Court</th><th>Allocated</th><th>Attended</th><th>Paid (att)</th><th>Not paid (att)</th><th>Unknown (att)</th><th>Targeted</th><th>Other session</th><th>Global</th>';
    const rows = sessPartData.sessions.map(s => `
      <tr>
        <td>${formatDate(s.date)}</td>
        <td>${formatTime(s.startTime)}</td>
        <td>${(s.programTypeName || '').replace(/</g, '&lt;')}</td>
        <td>${(s.court || '-')}</td>
        <td style="text-align:center">${s.allocated}</td>
        <td style="text-align:center">${s.attended}</td>
        <td style="text-align:center">${s.financial.paid}</td>
        <td style="text-align:center">${s.financial.notPaid}</td>
        <td style="text-align:center">${s.financial.unknown}</td>
        <td style="text-align:center">${s.source.targeted}</td>
        <td style="text-align:center">${s.source.other}</td>
        <td style="text-align:center">${s.source.global}</td>
      </tr>`).join('');
    const html = `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
    openPrintWindow('Session Participation', `${formatDate(sessPartData.startDate)} — ${formatDate(sessPartData.endDate)}`, html);
  };

  // CSV / Excel: per-session row + the per-participant detail rows
  // appended underneath each session for full traceability.
  const handleSessPartExcel = () => {
    if (!sessPartData || !sessPartData.sessions || sessPartData.sessions.length === 0) return;
    const lines = [];
    lines.push(['Date','Time','Program','Court','Allocated','Attended','Paid (att)','Not paid (att)','Unknown (att)','Targeted','Other session','Global'].join(','));
    sessPartData.sessions.forEach(s => {
      lines.push([
        s.date, s.startTime,
        `"${(s.programTypeName || '').replace(/"/g, '""')}"`,
        `"${(s.court || '').replace(/"/g, '""')}"`,
        s.allocated, s.attended,
        s.financial.paid, s.financial.notPaid, s.financial.unknown,
        s.source.targeted, s.source.other, s.source.global
      ].join(','));
      // Drill-down rows for traceability.
      lines.push(['','','Participant','Age group','Attended','Financial','Source'].join(','));
      s.participants.forEach(p => {
        const fin = p.isFinancial === true ? 'Paid' : p.isFinancial === false ? 'Not paid' : 'Unknown';
        const nameSafe = `${p.firstName || ''} ${p.lastName || ''}`.trim().replace(/"/g, '""');
        lines.push([
          '', '',
          `"${nameSafe}"`,
          p.ageGroup || '',
          p.attended ? 'Yes' : 'No',
          fin,
          p.source
        ].join(','));
      });
      lines.push('');
    });
    downloadCsv(`session-participation-${sessPartData.startDate}_${sessPartData.endDate}.csv`, lines.join('\n'));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  // === SHARED EXPORT HELPERS ===
  const downloadCsv = (filename, csvContent) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openPrintWindow = (title, subtitle, tableHtml) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { setError('Please allow pop-ups to download the PDF'); return; }
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
      body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
      h1 { color: #A4161A; font-size: 22px; margin-bottom: 5px; }
      .subtitle { color: #666; font-size: 14px; margin-bottom: 20px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th { background: #A4161A; color: white; text-align: left; padding: 10px; font-size: 13px; }
      td { padding: 8px 10px; border-bottom: 1px solid #e0e0e0; font-size: 13px; vertical-align: top; }
      tr:nth-child(even) { background: #f9f9f9; }
      .total-row { background: #e8eaf6 !important; font-weight: 600; }
      @media print { body { padding: 0; } }
    </style></head><body>
      <h1>SwishIQ - ${title}</h1>
      <div class="subtitle">${subtitle}</div>
      ${tableHtml}
    </body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  // === PAY REPORT EXCEL ===
  const handlePayReportExcel = () => {
    if (!reportData) return;
    const rows = [];
    if (reportType === 'both') {
      rows.push(['Name', 'Email', 'Date', 'Role', 'Qualification', 'Games', 'Forecast', 'Actual']);
      reportData.referees.forEach(ref => {
        ref.dailyBreakdown.forEach(e => {
          rows.push([ref.refereeName, ref.email, e.date, ROLE_LABELS[e.assignmentType] || e.assignmentType, e.qualificationLevel, e.gameCount, e.forecast.toFixed(2), e.actual.toFixed(2)]);
        });
        rows.push([ref.refereeName, ref.email, '', '', '', 'TOTAL', ref.forecastTotal.toFixed(2), ref.actualTotal.toFixed(2)]);
      });
      rows.push(['', '', '', '', '', 'GRAND TOTAL', reportData.forecastGrandTotal.toFixed(2), reportData.actualGrandTotal.toFixed(2)]);
    } else if (reportType === 'forecast') {
      rows.push(['Name', 'Email', 'Date', 'Role', 'Qualification', 'Games', 'Forecast']);
      reportData.referees.forEach(ref => {
        ref.dailyBreakdown.forEach(e => {
          rows.push([ref.refereeName, ref.email, e.date, ROLE_LABELS[e.assignmentType] || e.assignmentType, e.qualificationLevel, e.gameCount, e.forecast.toFixed(2)]);
        });
        rows.push([ref.refereeName, ref.email, '', '', '', 'TOTAL', ref.forecastTotal.toFixed(2)]);
      });
      rows.push(['', '', '', '', '', 'GRAND TOTAL', reportData.forecastGrandTotal.toFixed(2)]);
    } else {
      rows.push(['Name', 'Email', 'Date', 'Role', 'Qualification', 'Games', 'Actual']);
      reportData.referees.forEach(ref => {
        ref.dailyBreakdown.forEach(e => {
          rows.push([ref.refereeName, ref.email, e.date, ROLE_LABELS[e.assignmentType] || e.assignmentType, e.qualificationLevel, e.gameCount, e.actual.toFixed(2)]);
        });
        rows.push([ref.refereeName, ref.email, '', '', '', 'TOTAL', ref.actualTotal.toFixed(2)]);
      });
      rows.push(['', '', '', '', '', 'GRAND TOTAL', reportData.actualGrandTotal.toFixed(2)]);
    }
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    downloadCsv(`pay-report-${reportData.startDate}-to-${reportData.endDate}.csv`, csv);
  };

  // === COACHING ALLOCATION PDF & EXCEL ===
  const handleCoachingPdf = () => {
    if (!coachingData) return;
    let tableHtml = '';
    coachingData.programTypes.forEach(pt => {
      tableHtml += `<h3 style="margin:1rem 0 0.5rem;">${pt.programTypeName} — Budget: $${pt.weeklyBudget.toFixed(2)} | Cost: $${pt.totalCost.toFixed(2)} | ${pt.variance >= 0 ? 'Under' : 'Over'}: $${Math.abs(pt.variance).toFixed(2)}</h3>`;
      tableHtml += '<table><thead><tr><th>Date</th><th>Time</th><th>Duration</th><th>Coach</th><th>Qualification</th><th>Rate</th><th>Cost</th><th>Attended</th></tr></thead><tbody>';
      pt.programs.forEach(prog => {
        if (prog.coaches.length > 0) {
          prog.coaches.forEach((coach, idx) => {
            tableHtml += '<tr>';
            if (idx === 0) {
              tableHtml += `<td rowspan="${prog.coaches.length}">${formatDate(prog.date)}</td><td rowspan="${prog.coaches.length}">${formatTime(prog.startTime)}</td><td rowspan="${prog.coaches.length}">${prog.durationMinutes} min</td>`;
            }
            tableHtml += `<td>${coach.coachName}</td><td>${coach.qualificationLevel || '-'}</td><td>$${coach.payPerHour.toFixed(2)}/hr</td><td>$${coach.cost.toFixed(2)}</td><td>${coach.attended ? '✓' : '-'}</td></tr>`;
          });
        } else {
          tableHtml += `<tr><td>${formatDate(prog.date)}</td><td>${formatTime(prog.startTime)}</td><td>${prog.durationMinutes} min</td><td colspan="4" style="color:#999;">No coaches assigned</td><td>-</td></tr>`;
        }
      });
      tableHtml += `<tr class="total-row"><td colspan="6" style="text-align:right;">Total:</td><td>$${pt.totalCost.toFixed(2)}</td><td></td></tr></tbody></table>`;
    });
    tableHtml += `<div style="margin-top:1rem;padding:1rem;background:#f5f5f5;border-radius:4px;"><strong>Grand Total — Budget: $${coachingData.grandTotalBudget.toFixed(2)} | Cost: $${coachingData.grandTotalCost.toFixed(2)} | ${coachingData.grandTotalVariance >= 0 ? 'Under' : 'Over'} Budget: $${Math.abs(coachingData.grandTotalVariance).toFixed(2)}</strong></div>`;
    openPrintWindow('Coaching Allocation', `${formatDate(coachingData.weekStartDate)} — ${formatDate(coachingData.weekEndDate)}`, tableHtml);
  };

  const handleCoachingExcel = () => {
    if (!coachingData) return;
    const rows = [['Program Type', 'Date', 'Time', 'Duration (min)', 'Coach', 'Qualification', 'Rate/hr', 'Cost', 'Attended']];
    coachingData.programTypes.forEach(pt => {
      pt.programs.forEach(prog => {
        if (prog.coaches.length > 0) {
          prog.coaches.forEach(coach => {
            rows.push([pt.programTypeName, prog.date, formatTime(prog.startTime), prog.durationMinutes, coach.coachName, coach.qualificationLevel || '', coach.payPerHour.toFixed(2), coach.cost.toFixed(2), coach.attended ? 'Yes' : 'No']);
          });
        } else {
          rows.push([pt.programTypeName, prog.date, formatTime(prog.startTime), prog.durationMinutes, 'No coach assigned', '', '', '', '']);
        }
      });
      rows.push([pt.programTypeName + ' TOTAL', '', '', '', '', '', '', pt.totalCost.toFixed(2), '']);
    });
    rows.push(['GRAND TOTAL', '', '', '', '', 'Budget: ' + coachingData.grandTotalBudget.toFixed(2), '', coachingData.grandTotalCost.toFixed(2), '']);
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    downloadCsv(`coaching-allocation-${coachingData.weekStartDate}.csv`, csv);
  };

  // === PARTICIPANT ATTENDANCE PDF & EXCEL ===
  const handlePartAttPdf = () => {
    if (!partAttData) return;
    let tableHtml = '<table><thead><tr><th>Participant</th><th>Age Group</th><th>Program Type</th><th style="text-align:center">Scheduled</th><th style="text-align:center">Attended</th><th style="text-align:center">Rate</th></tr></thead><tbody>';
    partAttData.participants.forEach(p => {
      p.programTypes.forEach((pt, idx) => {
        tableHtml += '<tr>';
        if (idx === 0) {
          tableHtml += `<td rowspan="${p.programTypes.length}" style="font-weight:600;vertical-align:top">${p.firstName} ${p.lastName}</td><td rowspan="${p.programTypes.length}" style="vertical-align:top">${p.ageGroup || '-'}</td>`;
        }
        const rate = pt.scheduled > 0 ? Math.round((pt.attended / pt.scheduled) * 100) + '%' : '-';
        tableHtml += `<td>${pt.programTypeName}</td><td style="text-align:center">${pt.scheduled}</td><td style="text-align:center">${pt.attended}</td><td style="text-align:center">${rate}</td></tr>`;
      });
    });
    const grandRate = partAttData.grandTotalScheduled > 0 ? Math.round((partAttData.grandTotalAttended / partAttData.grandTotalScheduled) * 100) + '%' : '-';
    tableHtml += `<tr class="total-row"><td colspan="3" style="font-weight:700">Grand Total</td><td style="text-align:center;font-weight:700">${partAttData.grandTotalScheduled}</td><td style="text-align:center;font-weight:700">${partAttData.grandTotalAttended}</td><td style="text-align:center;font-weight:700">${grandRate}</td></tr>`;
    tableHtml += '</tbody></table>';
    const startStr = new Date(partAttData.startDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const endStr = new Date(partAttData.endDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    openPrintWindow('Participant Attendance', `${startStr} — ${endStr}`, tableHtml);
  };

  const handlePartAttExcel = () => {
    if (!partAttData) return;
    const rows = [['Participant', 'Age Group', 'Program Type', 'Scheduled', 'Attended', 'Rate']];
    partAttData.participants.forEach(p => {
      p.programTypes.forEach(pt => {
        const rate = pt.scheduled > 0 ? Math.round((pt.attended / pt.scheduled) * 100) + '%' : '-';
        rows.push([`${p.firstName} ${p.lastName}`, p.ageGroup || '', pt.programTypeName, pt.scheduled, pt.attended, rate]);
      });
      if (p.programTypes.length > 1) {
        const total = p.totalScheduled > 0 ? Math.round((p.totalAttended / p.totalScheduled) * 100) + '%' : '-';
        rows.push([`${p.firstName} ${p.lastName} TOTAL`, '', '', p.totalScheduled, p.totalAttended, total]);
      }
    });
    const grandRate = partAttData.grandTotalScheduled > 0 ? Math.round((partAttData.grandTotalAttended / partAttData.grandTotalScheduled) * 100) + '%' : '-';
    rows.push(['GRAND TOTAL', '', '', partAttData.grandTotalScheduled, partAttData.grandTotalAttended, grandRate]);
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    downloadCsv(`participant-attendance-${partAttData.startDate}-to-${partAttData.endDate}.csv`, csv);
  };

  // === PAY MATRIX ===
  const PM_ROLE_OPTIONS = [
    { key: 'referee', label: 'Referee' },
    { key: 'benchOfficial', label: 'Bench Official' },
    { key: 'courtOfficial', label: 'Court Official' },
    { key: 'coach', label: 'Coach' },
  ];
  const togglePmRole = (key) => setPmRoles(prev =>
    prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);

  const handleGeneratePayMatrix = async () => {
    if (pmEnd < pmStart) { setPmError('End date must be on or after the start date.'); return; }
    if (pmRoles.length === 0) { setPmError('Select at least one role.'); return; }
    setPmLoading(true); setPmError(''); setPmData(null); setPmExpanded({});
    try {
      const params = new URLSearchParams({
        startDate: pmStart, endDate: pmEnd,
        costType: pmCostType, detailed: pmDetailed ? 'true' : 'false',
        roles: pmRoles.join(','),
      });
      if (pmPeople.length > 0) params.set('personIds', pmPeople.join(','));
      const res = await fetch(`${API_BASE}/reports/pay-matrix?${params.toString()}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setPmError(e.error || `Failed to generate (HTTP ${res.status})`);
      } else {
        setPmData(await res.json());
      }
    } catch (err) { setPmError(err.message); }
    finally { setPmLoading(false); }
  };

  // Cell text for the chosen cost type. cell = {forecast, actual} | undefined.
  const pmCell = (cell) => {
    if (!cell) return '';
    const f = `$${(cell.forecast || 0).toFixed(2)}`;
    const a = `$${(cell.actual || 0).toFixed(2)}`;
    if (pmCostType === 'forecast') return f;
    if (pmCostType === 'actual') return a;
    return `${f} / ${a}`;
  };
  const pmDateLabel = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  };
  const pmCostHeader = pmCostType === 'both' ? 'Forecast / Actual'
    : pmCostType === 'forecast' ? 'Forecast' : 'Actual';

  const pmColTotal = (d) => {
    const c = pmData.columnTotals[d] || { forecast: 0, actual: 0 };
    return pmCell(c);
  };

  const exportPayMatrixCsv = () => {
    if (!pmData) return;
    const head = ['Person', 'Roles', ...pmData.days.map(pmDateLabel),
      pmCostType === 'both' ? 'Total Forecast' : 'Total',
      ...(pmCostType === 'both' ? ['Total Actual'] : [])];
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const lines = [head.map(esc).join(',')];
    for (const p of pmData.persons) {
      const row = [p.name, p.roles];
      for (const d of pmData.days) {
        const c = p.perDay[d];
        if (!c) { row.push(''); continue; }
        row.push(pmCostType === 'actual' ? c.actual.toFixed(2)
          : pmCostType === 'forecast' ? c.forecast.toFixed(2)
          : `${c.forecast.toFixed(2)} / ${c.actual.toFixed(2)}`);
      }
      if (pmCostType === 'both') { row.push(p.totalForecast.toFixed(2), p.totalActual.toFixed(2)); }
      else { row.push((pmCostType === 'actual' ? p.totalActual : p.totalForecast).toFixed(2)); }
      lines.push(row.map(esc).join(','));
    }
    const totalRow = ['Column Total', ''];
    for (const d of pmData.days) {
      const c = pmData.columnTotals[d] || { forecast: 0, actual: 0 };
      totalRow.push(pmCostType === 'actual' ? c.actual.toFixed(2)
        : pmCostType === 'forecast' ? c.forecast.toFixed(2)
        : `${c.forecast.toFixed(2)} / ${c.actual.toFixed(2)}`);
    }
    if (pmCostType === 'both') { totalRow.push(pmData.grandForecast.toFixed(2), pmData.grandActual.toFixed(2)); }
    else { totalRow.push((pmCostType === 'actual' ? pmData.grandActual : pmData.grandForecast).toFixed(2)); }
    lines.push(totalRow.map(esc).join(','));
    downloadCsv(`pay-matrix-${pmData.startDate}-to-${pmData.endDate}.csv`, lines.join('\n'));
  };

  const exportPayMatrixPdf = () => {
    if (!pmData) return;
    const th = ['Person', ...pmData.days.map(pmDateLabel), 'Total'];
    let html = '<table><thead><tr>' + th.map(h => `<th>${h}</th>`).join('') + '</tr></thead><tbody>';
    for (const p of pmData.persons) {
      html += `<tr><td>${p.name}</td>` +
        pmData.days.map(d => `<td>${pmCell(p.perDay[d]) || ''}</td>`).join('') +
        `<td>${pmCostType === 'both' ? `$${p.totalForecast.toFixed(2)} / $${p.totalActual.toFixed(2)}` : `$${(pmCostType === 'actual' ? p.totalActual : p.totalForecast).toFixed(2)}`}</td></tr>`;
    }
    html += `<tr class="total-row"><td>Column Total</td>` +
      pmData.days.map(d => `<td>${pmColTotal(d)}</td>`).join('') +
      `<td>${pmCostType === 'both' ? `$${pmData.grandForecast.toFixed(2)} / $${pmData.grandActual.toFixed(2)}` : `$${(pmCostType === 'actual' ? pmData.grandActual : pmData.grandForecast).toFixed(2)}`}</td></tr>`;
    html += '</tbody></table>';
    openPrintWindow('Pay Matrix', `${pmData.startDate} to ${pmData.endDate} | ${pmCostHeader}`, html);
  };

  return (
    <div className="reports">
      <div className="reports-tabs">
        <button className={activeTab === 'pay-report' ? 'active' : ''} onClick={() => setActiveTab('pay-report')}>Pay Report</button>
        <button className={activeTab === 'coaching-allocation' ? 'active' : ''} onClick={() => setActiveTab('coaching-allocation')}>Coaching Allocation</button>
        <button className={activeTab === 'participant-attendance' ? 'active' : ''} onClick={() => setActiveTab('participant-attendance')}>Participant Attendance</button>
        <button className={activeTab === 'session-participation' ? 'active' : ''} onClick={() => setActiveTab('session-participation')}>Session Participation</button>
        <button className={activeTab === 'pay-matrix' ? 'active' : ''} onClick={() => setActiveTab('pay-matrix')}>Pay Matrix</button>
        <button className={activeTab === 'court-supervisors' ? 'active' : ''} onClick={() => setActiveTab('court-supervisors')}>Court Supervisors</button>
        <button className={activeTab === 'shift-reliability' ? 'active' : ''} onClick={() => setActiveTab('shift-reliability')}>Shift Reliability</button>
      </div>

      {/* PAY REPORT TAB */}
      {activeTab === 'pay-report' && (
        <>
          <div className="report-controls">
            <div className="control-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={startDate}
                min={reportsMinDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(startDate)}</small>
            </div>

            <div className="control-group">
              <label>End Date:</label>
              <input
                type="date"
                value={endDate}
                min={reportsMinDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(endDate)}</small>
            </div>

            <div className="control-group">
              <label>Show:</label>
              <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
                <option value="both">Forecast vs Actual</option>
                <option value="forecast">Forecast Only</option>
                <option value="actual">Actual Only</option>
              </select>
            </div>

            <div className="control-group">
              <label>Filter by Person:</label>
              <select value={selectedPerson} onChange={(e) => setSelectedPerson(e.target.value)}>
                <option value="">All People</option>
                {people.map(person => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <label>Filter by Role:</label>
              <select value={selectedQualification} onChange={(e) => setSelectedQualification(e.target.value)}>
                <option value="">All Roles</option>
                <option value="referee">Referee</option>
                <option value="benchOfficial">Bench Official</option>
                <option value="courtOfficial">Court Official</option>
                <option value="coach">Coach</option>
              </select>
            </div>

            <button className="btn-generate" onClick={handleGenerateReport}>
              Generate Report
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {loading && <div className="loading">Generating report...</div>}

          {reportData && (
            <div className="report-content" ref={reportRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Pay Report: {reportData.startDate} to {reportData.endDate}</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn-export" onClick={handleDownloadPdf}>Save as PDF</button>
                  <button className="btn-export" onClick={handlePayReportExcel}>Save as Excel</button>
                </div>
              </div>
              <p className="report-type-info">Showing {reportType === 'both' ? 'Forecast vs Actual' : reportType === 'forecast' ? 'Forecast (Expected Costs)' : 'Actual (Based on Attendance)'} Costs for Referees & Bench Officials</p>

              {reportData.referees.length === 0 ? (
                <div className="no-report">No data available for this date range</div>
              ) : (
                <>
                  {reportType === 'both' ? (
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Daily Breakdown</th>
                          <th>Forecast</th>
                          <th>Actual</th>
                          <th>Difference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.referees.map(referee => (
                          <tr key={referee.refereeId}>
                            <td>{referee.refereeName}</td>
                            <td>{referee.email}</td>
                            <td>{renderDailyBreakdown(referee.dailyBreakdown, 'both')}</td>
                            <td>${referee.forecastTotal.toFixed(2)}</td>
                            <td>${referee.actualTotal.toFixed(2)}</td>
                            <td style={{ color: referee.actualTotal < referee.forecastTotal ? '#4caf50' : referee.actualTotal > referee.forecastTotal ? '#f44336' : '#666' }}>
                              ${(referee.actualTotal - referee.forecastTotal).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr className="grand-total">
                          <td colSpan="3">Grand Total</td>
                          <td>${reportData.forecastGrandTotal.toFixed(2)}</td>
                          <td>${reportData.actualGrandTotal.toFixed(2)}</td>
                          <td style={{ color: reportData.actualGrandTotal < reportData.forecastGrandTotal ? '#4caf50' : reportData.actualGrandTotal > reportData.forecastGrandTotal ? '#f44336' : '#666' }}>
                            ${(reportData.actualGrandTotal - reportData.forecastGrandTotal).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : reportType === 'forecast' ? (
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Daily Breakdown</th>
                          <th>Total Forecast</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.referees.map(referee => (
                          <tr key={referee.refereeId}>
                            <td>{referee.refereeName}</td>
                            <td>{referee.email}</td>
                            <td>{renderDailyBreakdown(referee.dailyBreakdown, 'forecast')}</td>
                            <td>${referee.forecastTotal.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="grand-total">
                          <td colSpan="3">Grand Total</td>
                          <td>${reportData.forecastGrandTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Daily Breakdown</th>
                          <th>Total Actual</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.referees.map(referee => (
                          <tr key={referee.refereeId}>
                            <td>{referee.refereeName}</td>
                            <td>{referee.email}</td>
                            <td>{renderDailyBreakdown(referee.dailyBreakdown, 'actual')}</td>
                            <td>${referee.actualTotal.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="grand-total">
                          <td colSpan="3">Grand Total</td>
                          <td>${reportData.actualGrandTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* COACHING ALLOCATION TAB */}
      {activeTab === 'coaching-allocation' && (
        <>
          <div className="report-controls">
            <div className="control-group">
              <label>Week Starting (Monday):</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button className="btn-week-nav" onClick={() => navigateWeek(-1)}>&larr;</button>
                <input
                  type="date"
                  value={coachingWeekDate}
                  onChange={(e) => setCoachingWeekDate(e.target.value)}
                />
                <button className="btn-week-nav" onClick={() => navigateWeek(1)}>&rarr;</button>
                <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(coachingWeekDate)}</small>
              </div>
            </div>

            <button className="btn-generate" onClick={handleGenerateCoachingReport}>
              Generate Report
            </button>
          </div>

          {coachingError && <div className="error-message">{coachingError}</div>}

          {coachingLoading && <div className="loading">Generating coaching allocation report...</div>}

          {coachingData && (
            <div className="report-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>
                  Coaching Allocation: {formatDate(coachingData.weekStartDate)} — {formatDate(coachingData.weekEndDate)}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn-export" onClick={handleCoachingPdf}>Save as PDF</button>
                  <button className="btn-export" onClick={handleCoachingExcel}>Save as Excel</button>
                </div>
              </div>

              {coachingData.programTypes.length === 0 ? (
                <div className="no-report">No coaching programs found for this week</div>
              ) : (
                <>
                  {coachingData.programTypes.map(pt => (
                    <div key={pt.programTypeId} className="coaching-program-type">
                      <div className="coaching-pt-header" onClick={() => toggleProgramType(pt.programTypeId)}>
                        <div className="coaching-pt-title">
                          <span className="coaching-pt-expand">{expandedProgramTypes[pt.programTypeId] ? '▼' : '▶'}</span>
                          <strong>{pt.programTypeName}</strong>
                          <span className="coaching-pt-count">({pt.programs.length} session{pt.programs.length !== 1 ? 's' : ''})</span>
                        </div>
                        <div className="coaching-pt-summary">
                          <span className="coaching-pt-budget">Budget: ${pt.weeklyBudget.toFixed(2)}</span>
                          <span className="coaching-pt-cost">Cost: ${pt.totalCost.toFixed(2)}</span>
                          <span className={`coaching-pt-variance ${pt.variance >= 0 ? 'variance-positive' : 'variance-negative'}`}>
                            {pt.variance >= 0 ? 'Under' : 'Over'}: ${Math.abs(pt.variance).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {expandedProgramTypes[pt.programTypeId] && (
                        <div className="coaching-pt-details">
                          <table className="report-table coaching-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Duration</th>
                                <th>Coach</th>
                                <th>Qualification</th>
                                <th>Rate</th>
                                <th>Cost</th>
                                <th>Attended</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pt.programs.map(prog => (
                                <React.Fragment key={prog.programId}>
                                  {prog.coaches.length > 0 ? prog.coaches.map((coach, idx) => (
                                    <tr key={`${prog.programId}-${coach.assignmentId}`}>
                                      {idx === 0 && (
                                        <>
                                          <td rowSpan={prog.coaches.length}>{formatDate(prog.date)}</td>
                                          <td rowSpan={prog.coaches.length}>{formatTime(prog.startTime)}</td>
                                          <td rowSpan={prog.coaches.length}>{prog.durationMinutes} min</td>
                                        </>
                                      )}
                                      <td>{coach.coachName}</td>
                                      <td>{coach.qualificationLevel || '-'}</td>
                                      <td>${coach.payPerHour.toFixed(2)}/hr</td>
                                      <td>${coach.cost.toFixed(2)}</td>
                                      <td>{coach.attended ? '✓' : '-'}</td>
                                    </tr>
                                  )) : (
                                    <tr key={prog.programId}>
                                      <td>{formatDate(prog.date)}</td>
                                      <td>{formatTime(prog.startTime)}</td>
                                      <td>{prog.durationMinutes} min</td>
                                      <td colSpan="4" style={{ color: '#999', fontStyle: 'italic' }}>No coaches assigned</td>
                                      <td>-</td>
                                    </tr>
                                  )}
                                  {prog.coaches.length > 1 && (
                                    <tr className="program-subtotal">
                                      <td colSpan="6" style={{ textAlign: 'right', fontWeight: 600 }}>Session Total:</td>
                                      <td style={{ fontWeight: 600 }}>${prog.programCost.toFixed(2)}</td>
                                      <td></td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                              <tr className="coaching-pt-total">
                                <td colSpan="6" style={{ textAlign: 'right', fontWeight: 700 }}>{pt.programTypeName} Total:</td>
                                <td style={{ fontWeight: 700 }}>${pt.totalCost.toFixed(2)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Grand Total Summary */}
                  <div className="coaching-grand-total">
                    <div className="coaching-grand-row">
                      <span>Total Budget:</span>
                      <strong>${coachingData.grandTotalBudget.toFixed(2)}</strong>
                    </div>
                    <div className="coaching-grand-row">
                      <span>Total Cost:</span>
                      <strong>${coachingData.grandTotalCost.toFixed(2)}</strong>
                    </div>
                    <div className="coaching-grand-row">
                      <span>Total Variance:</span>
                      <strong className={coachingData.grandTotalVariance >= 0 ? 'variance-positive' : 'variance-negative'}>
                        {coachingData.grandTotalVariance >= 0 ? '+' : '-'}${Math.abs(coachingData.grandTotalVariance).toFixed(2)}
                        {coachingData.grandTotalVariance >= 0 ? ' (Under Budget)' : ' (Over Budget)'}
                      </strong>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* PARTICIPANT ATTENDANCE TAB */}
      {activeTab === 'participant-attendance' && (
        <>
          <div className="report-controls">
            <div className="control-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={partAttStartDate}
                min={reportsMinDate}
                onChange={(e) => setPartAttStartDate(e.target.value)}
              />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(partAttStartDate)}</small>
            </div>
            <div className="control-group">
              <label>End Date:</label>
              <input
                type="date"
                value={partAttEndDate}
                min={reportsMinDate}
                onChange={(e) => setPartAttEndDate(e.target.value)}
              />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(partAttEndDate)}</small>
            </div>
            <div className="control-group">
              <label>Program Type:</label>
              <select value={partAttProgramType} onChange={(e) => setPartAttProgramType(e.target.value)}>
                <option value="">All Program Types</option>
                {programTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>Participant:</label>
              <select value={partAttParticipant} onChange={(e) => setPartAttParticipant(e.target.value)}>
                <option value="">All Participants</option>
                {allParticipants.map(p => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>&nbsp;</label>
              <button className="btn-generate" onClick={handleGenerateParticipantAttendanceReport}>
                Generate Report
              </button>
            </div>
          </div>

          {partAttError && <div className="error-message">{partAttError}</div>}
          {partAttLoading && <div className="loading">Generating participant attendance report...</div>}

          {partAttData && !partAttLoading && (
            <div className="report-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>
                  Participant Attendance: {new Date(partAttData.startDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} — {new Date(partAttData.endDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn-export" onClick={handlePartAttPdf}>Save as PDF</button>
                  <button className="btn-export" onClick={handlePartAttExcel}>Save as Excel</button>
                </div>
              </div>
              {partAttData.participants.length === 0 ? (
                <div className="no-report">No attendance data found for this date range.</div>
              ) : (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Participant</th>
                      <th>Age Group</th>
                      <th>Program Type</th>
                      <th style={{ textAlign: 'center' }}>Scheduled</th>
                      <th style={{ textAlign: 'center' }}>Attended</th>
                      <th style={{ textAlign: 'center' }}>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partAttData.participants.map(p => (
                      <React.Fragment key={p.participantId}>
                        {p.programTypes.map((pt, idx) => (
                          <tr key={`${p.participantId}-${pt.programTypeId}`}>
                            {idx === 0 && (
                              <>
                                <td rowSpan={p.programTypes.length + (p.programTypes.length > 1 ? 1 : 0)} style={{ fontWeight: 600, verticalAlign: 'top' }}>
                                  {p.firstName} {p.lastName}
                                </td>
                                <td rowSpan={p.programTypes.length + (p.programTypes.length > 1 ? 1 : 0)} style={{ verticalAlign: 'top' }}>
                                  {p.ageGroup || '-'}
                                </td>
                              </>
                            )}
                            <td>{pt.programTypeName}</td>
                            <td style={{ textAlign: 'center' }}>{pt.scheduled}</td>
                            <td style={{ textAlign: 'center' }}>{pt.attended}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`attendance-rate ${pt.scheduled > 0 && pt.attended / pt.scheduled >= 0.8 ? 'good' : pt.scheduled > 0 && pt.attended / pt.scheduled >= 0.5 ? 'moderate' : pt.scheduled > 0 ? 'poor' : ''}`}>
                                {pt.scheduled > 0 ? Math.round((pt.attended / pt.scheduled) * 100) + '%' : '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {p.programTypes.length > 1 && (
                          <tr className="participant-subtotal">
                            <td style={{ fontStyle: 'italic', fontWeight: 600 }}>Total</td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>{p.totalScheduled}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>{p.totalAttended}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {p.totalScheduled > 0 ? Math.round((p.totalAttended / p.totalScheduled) * 100) + '%' : '-'}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    <tr className="coaching-grand-total">
                      <td colSpan={3} style={{ fontWeight: 700 }}>Grand Total</td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>{partAttData.grandTotalScheduled}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>{partAttData.grandTotalAttended}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>
                        {partAttData.grandTotalScheduled > 0 ? Math.round((partAttData.grandTotalAttended / partAttData.grandTotalScheduled) * 100) + '%' : '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'session-participation' && (
        <>
          <div className="report-controls">
            <div className="control-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={sessPartStartDate}
                min={reportsMinDate}
                onChange={(e) => setSessPartStartDate(e.target.value)}
              />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(sessPartStartDate)}</small>
            </div>
            <div className="control-group">
              <label>End Date:</label>
              <input
                type="date"
                value={sessPartEndDate}
                min={reportsMinDate}
                onChange={(e) => setSessPartEndDate(e.target.value)}
              />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(sessPartEndDate)}</small>
            </div>
            <div className="control-group">
              <label>Program Type:</label>
              <select value={sessPartProgramType} onChange={(e) => setSessPartProgramType(e.target.value)}>
                <option value="">All Program Types</option>
                {programTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>&nbsp;</label>
              <button className="btn-generate" onClick={handleGenerateSessionParticipationReport}>
                Generate Report
              </button>
            </div>
          </div>

          {sessPartError && <div className="error-message">{sessPartError}</div>}
          {sessPartLoading && <div className="loading">Generating session participation report...</div>}

          {sessPartData && !sessPartLoading && (
            <div className="report-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>
                  Session Participation: {new Date(sessPartData.startDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} — {new Date(sessPartData.endDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn-export" onClick={handleSessPartPdf}>Save as PDF</button>
                  <button className="btn-export" onClick={handleSessPartExcel}>Save as Excel</button>
                </div>
              </div>

              {sessPartData.sessions.length === 0 ? (
                <div className="no-report">No sessions found for this date range.</div>
              ) : (
                <>
                  {/* Legend */}
                  <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#f5f5f5', borderRadius: 6, fontSize: '0.75rem', color: '#555', lineHeight: 1.5 }}>
                    <strong>Source:</strong>
                    <span style={{ marginLeft: 8 }}><strong>Targeted</strong> = participant's CSV target matches this session's day + time</span>
                    <span style={{ marginLeft: 8 }}>·</span>
                    <span style={{ marginLeft: 8 }}><strong>Other session</strong> = enrolled in this program but on a different day/time (added via Select Other Participants)</span>
                    <span style={{ marginLeft: 8 }}>·</span>
                    <span style={{ marginLeft: 8 }}><strong>Global</strong> = no session targets (manual add via People or My Portal Programs)</span>
                  </div>
                  <table className="report-table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Program</th>
                        <th>Court</th>
                        <th style={{ textAlign: 'center' }}>Allocated</th>
                        <th style={{ textAlign: 'center' }}>Attended</th>
                        <th style={{ textAlign: 'center', background: '#e8f5e9' }}>Paid</th>
                        <th style={{ textAlign: 'center', background: '#ffebee' }}>Not paid</th>
                        <th style={{ textAlign: 'center', background: '#f5f5f5' }}>Unknown</th>
                        <th style={{ textAlign: 'center' }}>Targeted</th>
                        <th style={{ textAlign: 'center' }}>Other session</th>
                        <th style={{ textAlign: 'center' }}>Global</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessPartData.sessions.map(s => {
                        const isExpanded = !!sessPartExpanded[s.programId];
                        return (
                          <React.Fragment key={s.programId}>
                            <tr>
                              <td>{formatDate(s.date)}</td>
                              <td>{formatTime(s.startTime)}</td>
                              <td>{s.programTypeName}</td>
                              <td>{s.court || '-'}</td>
                              <td style={{ textAlign: 'center', fontWeight: 600 }}>{s.allocated}</td>
                              <td style={{ textAlign: 'center', fontWeight: 600 }}>{s.attended}</td>
                              <td style={{ textAlign: 'center', background: s.financial.paid > 0 ? '#e8f5e9' : 'transparent' }}>{s.financial.paid}</td>
                              <td style={{ textAlign: 'center', background: s.financial.notPaid > 0 ? '#ffebee' : 'transparent' }}>{s.financial.notPaid}</td>
                              <td style={{ textAlign: 'center', background: s.financial.unknown > 0 ? '#f5f5f5' : 'transparent' }}>{s.financial.unknown}</td>
                              <td style={{ textAlign: 'center' }}>{s.source.targeted}</td>
                              <td style={{ textAlign: 'center', color: s.source.other > 0 ? '#1565c0' : 'inherit', fontWeight: s.source.other > 0 ? 600 : 'normal' }}>{s.source.other}</td>
                              <td style={{ textAlign: 'center', color: s.source.global > 0 ? '#7b1fa2' : 'inherit', fontWeight: s.source.global > 0 ? 600 : 'normal' }}>{s.source.global}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => setSessPartExpanded(prev => ({ ...prev, [s.programId]: !prev[s.programId] }))}
                                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', background: 'white', border: '1px solid #999', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                >
                                  {isExpanded ? 'Hide' : 'Details'}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={13} style={{ background: '#fafafa', padding: '0.5rem 0.75rem' }}>
                                  {s.participants.length === 0 ? (
                                    <em style={{ color: '#888' }}>No participants on this session.</em>
                                  ) : (
                                    <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                                          <th style={{ textAlign: 'left', padding: '0.3rem' }}>Participant</th>
                                          <th style={{ textAlign: 'left', padding: '0.3rem' }}>Age group</th>
                                          <th style={{ textAlign: 'center', padding: '0.3rem' }}>Attended</th>
                                          <th style={{ textAlign: 'center', padding: '0.3rem' }}>Financial</th>
                                          <th style={{ textAlign: 'center', padding: '0.3rem' }}>Source</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {s.participants.map(p => (
                                          <tr key={p.participantId} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '0.25rem 0.3rem' }}>{p.firstName} {p.lastName}</td>
                                            <td style={{ padding: '0.25rem 0.3rem' }}>{p.ageGroup || '-'}</td>
                                            <td style={{ textAlign: 'center', padding: '0.25rem 0.3rem', color: p.attended ? '#2e7d32' : '#999', fontWeight: p.attended ? 600 : 'normal' }}>
                                              {p.attended ? '✓' : '—'}
                                            </td>
                                            <td style={{ textAlign: 'center', padding: '0.25rem 0.3rem' }}>
                                              {p.isFinancial === true ? <span style={{ color: '#2e7d32', fontWeight: 600 }}>Paid</span>
                                                : p.isFinancial === false ? <span style={{ color: '#c62828', fontWeight: 600 }}>Not paid</span>
                                                : <span style={{ color: '#888' }}>Unknown</span>}
                                            </td>
                                            <td style={{ textAlign: 'center', padding: '0.25rem 0.3rem' }}>
                                              {p.source === 'targeted' ? <span style={{ color: '#555' }}>Targeted</span>
                                                : p.source === 'other' ? <span style={{ color: '#1565c0', fontWeight: 600 }}>Other session</span>
                                                : <span style={{ color: '#7b1fa2', fontWeight: 600 }}>Global</span>}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* PAY MATRIX TAB */}
      {activeTab === 'pay-matrix' && (
        <>
          <div className="report-controls" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className="control-group">
              <label>Start Date:</label>
              <input type="date" value={pmStart} min={reportsMinDate}
                onChange={(e) => { const v = e.target.value; setPmStart(v); if (pmEnd < v) setPmEnd(v); }} />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(pmStart)}</small>
            </div>
            <div className="control-group">
              <label>End Date:</label>
              <input type="date" value={pmEnd} min={pmStart}
                onChange={(e) => setPmEnd(e.target.value)} />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(pmEnd)}</small>
            </div>
            <div className="control-group">
              <label>Show:</label>
              <select value={pmCostType} onChange={(e) => setPmCostType(e.target.value)}>
                <option value="both">Forecast &amp; Actual</option>
                <option value="forecast">Forecast Only</option>
                <option value="actual">Actual Only</option>
              </select>
            </div>
            <div className="control-group">
              <label>Roles:</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {PM_ROLE_OPTIONS.map(r => (
                  <label key={r.key} style={{ fontSize: 13, fontWeight: 400, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="checkbox" checked={pmRoles.includes(r.key)} onChange={() => togglePmRole(r.key)} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="control-group">
              <label>People ({pmPeople.length === 0 ? 'all' : pmPeople.length} selected):</label>
              <select multiple value={pmPeople.map(String)} style={{ minHeight: 110, minWidth: 180 }}
                onChange={(e) => setPmPeople(Array.from(e.target.selectedOptions, o => parseInt(o.value, 10)))}>
                {people.filter(p => !p.isArchived).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {pmPeople.length > 0 && (
                <button className="btn-export" style={{ marginTop: 4 }} onClick={() => setPmPeople([])}>Clear (all people)</button>
              )}
            </div>
            <div className="control-group">
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={pmDetailed} onChange={(e) => setPmDetailed(e.target.checked)} />
                Detailed
              </label>
              <small style={{ color: '#666' }}>Show per-day games/shifts</small>
            </div>
            <button className="btn-generate" onClick={handleGeneratePayMatrix}>Generate Report</button>
          </div>

          {pmError && <div className="error-message">{pmError}</div>}
          {pmLoading && <div className="loading">Generating report...</div>}

          {pmData && (
            <div className="report-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Pay Matrix: {pmData.startDate} to {pmData.endDate}</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn-export" onClick={exportPayMatrixPdf}>Save as PDF</button>
                  <button className="btn-export" onClick={exportPayMatrixCsv}>Save as Excel</button>
                </div>
              </div>
              <p className="report-type-info">
                {pmCostHeader} · roles: {pmData.roles.length ? pmData.roles.map(r => ROLE_LABELS[r] || r).join(', ') : 'all'}
                {pmData.detailed ? ' · detailed' : ''}
              </p>

              {pmData.persons.length === 0 ? (
                <div className="no-report">No data for these filters.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th style={{ position: 'sticky', left: 0, background: '#A4161A' }}>Person</th>
                        {pmData.days.map(d => <th key={d} style={{ whiteSpace: 'nowrap' }}>{pmDateLabel(d)}</th>)}
                        <th style={{ whiteSpace: 'nowrap' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pmData.persons.map(p => (
                        <React.Fragment key={p.personId}>
                          <tr>
                            <td style={{ position: 'sticky', left: 0, background: '#fff', fontWeight: 600 }}>
                              {pmData.detailed ? (
                                <button
                                  onClick={() => setPmExpanded(prev => ({ ...prev, [p.personId]: !prev[p.personId] }))}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', fontWeight: 600, padding: 0 }}
                                  title="Show / hide daily detail"
                                >
                                  {pmExpanded[p.personId] ? '▾ ' : '▸ '}{p.name}
                                </button>
                              ) : p.name}
                              <div style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>{p.roles}</div>
                            </td>
                            {pmData.days.map(d => (
                              <td key={d} style={{ whiteSpace: 'nowrap' }}>{pmCell(p.perDay[d])}</td>
                            ))}
                            <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                              {pmCostType === 'both'
                                ? `$${p.totalForecast.toFixed(2)} / $${p.totalActual.toFixed(2)}`
                                : `$${(pmCostType === 'actual' ? p.totalActual : p.totalForecast).toFixed(2)}`}
                            </td>
                          </tr>
                          {pmData.detailed && pmExpanded[p.personId] && (
                            <tr>
                              <td colSpan={pmData.days.length + 2} style={{ background: '#fafafa' }}>
                                {pmData.days.filter(d => (p.detail || {})[d]).map(d => (
                                  <div key={d} style={{ marginBottom: 4 }}>
                                    <strong>{pmDateLabel(d)}:</strong>{' '}
                                    {(p.detail[d] || []).map((it, i) => (
                                      <span key={i} style={{ color: '#555', marginRight: 12, fontSize: 12 }}>
                                        {ROLE_LABELS[it.assignmentType] || it.assignmentType} ({it.qualificationLevel}){it.gameCount > 1 ? ` ×${it.gameCount}` : ''}:{' '}
                                        {pmCostType === 'actual' ? `$${it.actual.toFixed(2)}`
                                          : pmCostType === 'forecast' ? `$${it.forecast.toFixed(2)}`
                                          : `$${it.forecast.toFixed(2)} → $${it.actual.toFixed(2)}`}
                                      </span>
                                    ))}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      <tr className="total-row" style={{ background: '#e8eaf6', fontWeight: 600 }}>
                        <td style={{ position: 'sticky', left: 0, background: '#e8eaf6' }}>Column Total</td>
                        {pmData.days.map(d => <td key={d} style={{ whiteSpace: 'nowrap' }}>{pmColTotal(d)}</td>)}
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {pmCostType === 'both'
                            ? `$${pmData.grandForecast.toFixed(2)} / $${pmData.grandActual.toFixed(2)}`
                            : `$${(pmCostType === 'actual' ? pmData.grandActual : pmData.grandForecast).toFixed(2)}`}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* COURT SUPERVISORS TAB */}
      {activeTab === 'shift-reliability' && <ShiftReliabilityReport minDate={reportsMinDate} />}

      {activeTab === 'court-supervisors' && (
        <>
          <div className="report-controls">
            <div className="control-group">
              <label>Date:</label>
              <input type="date" value={csDate} min={reportsMinDate} onChange={(e) => setCsDate(e.target.value)} />
              <small style={{ color: '#1565c0', fontWeight: 600 }}>{dowLong(csDate)}</small>
            </div>
            {/* Both buttons live in one group so they sit together where
                the Generate button goes on the other tabs. */}
            <div className="control-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <button
                className="btn-generate"
                style={{ padding: '0.45rem 1.1rem', fontSize: '0.85rem' }}
                onClick={runCourtSupervisors}
                disabled={csLoading}
              >
                {csLoading ? 'Loading…' : 'Generate Report'}
              </button>
              {csData && (
                <button
                  className="btn-generate"
                  style={{ padding: '0.45rem 1.1rem', fontSize: '0.85rem' }}
                  onClick={handleCourtSupervisorsPdf}
                >
                  Export PDF
                </button>
              )}
            </div>
          </div>

          {csError && <div className="error-message">{csError}</div>}

          {csData && (
            <div className="report-container" style={{ marginTop: '1rem' }}>
              <p className="report-type-info">
                Court Supervisors Report — {csFmtDate(csData.startDate)} — {csData.gamesWithReports} game report(s) entered
              </p>

              {(() => {
                const th = { textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: '#333', background: '#fafafa', borderBottom: '2px solid #e0e0e0', fontSize: '0.85rem' };
                const td = { padding: '7px 10px', borderBottom: '1px solid #f0f0f0', fontSize: '0.85rem', verticalAlign: 'top', overflowWrap: 'break-word' };
                // Fixed widths for the four columns every section shares, so
                // the tables line up vertically down the page. table-layout
                // fixed makes the widths authoritative; the section-specific
                // trailing columns share whatever space remains.
                const colWidths = { 'Date': 140, 'Court': 85, 'Division': 160, 'Match': 250 };
                const gameTds = (r) => (
                  <>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.date} {r.time}</td>
                    <td style={td}>{r.court}</td>
                    <td style={td}>{r.division}</td>
                    <td style={td}>{r.match}</td>
                  </>
                );
                const sectionBlock = (title, rows, headCells, rowFn) => (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ color: '#A4161A', margin: '0 0 6px' }}>{title}</h3>
                    {rows.length === 0
                      ? <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>Nothing reported.</p>
                      : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <thead><tr>{headCells.map(h => <th key={h} style={{ ...th, width: colWidths[h] }}>{h}</th>)}</tr></thead>
                            <tbody>{rows.map((r, i) => <tr key={i}>{rowFn(r)}</tr>)}</tbody>
                          </table>
                        </div>
                      )}
                  </div>
                );
                return (
                  <>
                    {sectionBlock('Forfeits', csData.forfeits,
                      ['Date', 'Court', 'Division', 'Match', 'Forfeited by'],
                      r => <>{gameTds(r)}<td style={{ ...td, fontWeight: 700 }}>{r.forfeitTeam}</td></>)}
                    {sectionBlock('Tech Fouls', csData.techFouls,
                      ['Date', 'Court', 'Division', 'Match', 'Person', 'Fouls', 'Severity', 'Ejection', 'Comments'],
                      r => <>{gameTds(r)}<td style={td}>{r.name}{r.team ? ` (${r.team})` : ''}</td><td style={td}>{r.count}</td><td style={{ ...td, color: r.severity === 'High' ? '#c62828' : undefined, fontWeight: r.severity === 'High' ? 700 : 400 }}>{r.severity || 'Low'}</td><td style={{ ...td, color: r.ejection ? '#c62828' : undefined, fontWeight: r.ejection ? 700 : 400 }}>{r.ejection ? 'Yes' : 'No'}</td><td style={td}>{r.comments}</td></>)}
                    {sectionBlock('Injuries', csData.injuries,
                      ['Date', 'Court', 'Division', 'Match', 'Person', 'Injury', 'Treatment', 'Comments'],
                      r => <>{gameTds(r)}<td style={td}>{r.name}</td><td style={td}>{r.injury}</td><td style={td}>{r.treatment}</td><td style={td}>{r.comments}</td></>)}
                    {/* General Info: day-level Daily Notes render as plain
                        paragraphs (no game columns), followed by the
                        per-game notes table when there are any. */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ color: '#A4161A', margin: '0 0 6px' }}>General Info</h3>
                      {(csData.dayNotes || []).map((n, i) => (
                        <p key={i} style={{ margin: '0 0 8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                          {csData.startDate !== csData.endDate && <strong>{n.date}: </strong>}{n.notes}
                        </p>
                      ))}
                      {csData.generalInfo.length === 0 && (csData.dayNotes || []).length === 0 && (
                        <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>Nothing reported.</p>
                      )}
                      {csData.generalInfo.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <thead><tr>{['Date', 'Court', 'Division', 'Match', 'Notes'].map(h => <th key={h} style={{ ...th, width: colWidths[h] }}>{h}</th>)}</tr></thead>
                            <tbody>
                              {csData.generalInfo.map((r, i) => (
                                <tr key={i}>{gameTds(r)}<td style={{ ...td, whiteSpace: 'pre-wrap' }}>{r.notes}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
